import type { NavItem } from '@/components/layout/Sidebar';

export const NAV_FAVORITES_KEY = 'fattah_nav_favorites_v1';
export const NAV_FAVORITES_LIMIT = 8;

/** Pathname without query or trailing slash (except `/`). */
export function normalizeFavoritePath(path: string): string {
  const pathname = (path.split('?')[0] ?? '/').trim() || '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function readFavoritePaths(): string[] {
  try {
    const raw = localStorage.getItem(NAV_FAVORITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const paths: string[] = [];
    for (const value of parsed) {
      if (typeof value !== 'string') continue;
      const path = normalizeFavoritePath(value);
      if (!path || seen.has(path)) continue;
      seen.add(path);
      paths.push(path);
    }
    return paths;
  } catch {
    return [];
  }
}

export function isFavoritePath(
  path: string,
  favorites: string[] = readFavoritePaths(),
): boolean {
  const current = normalizeFavoritePath(path);
  return favorites.some((fav) => normalizeFavoritePath(fav) === current);
}

/**
 * Canonical favorite path for the current screen: matching nav item when
 * possible, otherwise the location pathname.
 */
export function favoritePathForLocation(pathname: string, allItems: NavItem[]): string {
  const current = normalizeFavoritePath(pathname);
  const exact = allItems.find((item) => normalizeFavoritePath(item.to) === current);
  if (exact) return normalizeFavoritePath(exact.to);

  let nested: NavItem | undefined;
  let nestedLen = 0;
  for (const item of allItems) {
    const itemPath = normalizeFavoritePath(item.to);
    if (itemPath === '/pos/dashboard') continue;
    if (!current.startsWith(`${itemPath}/`)) continue;
    if (itemPath.length > nestedLen) {
      nested = item;
      nestedLen = itemPath.length;
    }
  }
  if (nested) return normalizeFavoritePath(nested.to);
  return current;
}

export function toggleFavoritePath(path: string): string[] {
  const target = normalizeFavoritePath(path);
  const current = readFavoritePaths();
  const exists = current.some((fav) => normalizeFavoritePath(fav) === target);
  const next = exists
    ? current.filter((fav) => normalizeFavoritePath(fav) !== target)
    : [target, ...current].slice(0, NAV_FAVORITES_LIMIT);
  localStorage.setItem(NAV_FAVORITES_KEY, JSON.stringify(next));
  return next;
}

export function resolveNavItemsByPaths(paths: string[], allItems: NavItem[]): NavItem[] {
  const byPath = new Map(allItems.map((item) => [normalizeFavoritePath(item.to), item]));
  const seen = new Set<string>();
  const items: NavItem[] = [];
  for (const path of paths) {
    const key = normalizeFavoritePath(path);
    if (seen.has(key)) continue;
    const item = byPath.get(key);
    if (!item) continue;
    seen.add(key);
    items.push(item);
  }
  return items;
}
